import type { ReactNode } from 'react';
import {
  AppWindow,
  Boxes,
  Building2,
  CircleHelp,
  FileCode2,
  PanelsTopLeft,
  ShoppingCart,
  type LucideIcon,
} from 'lucide-react';
import { platformLabel } from '@cp/shared';
import { BRAND_MARKS, type BrandName } from '@/lib/brand-marks';

/**
 * A brand's mark in its colour. Marks too dark to read on the dark theme
 * (WordPress blue, Ghost black) are drawn in the text colour there instead
 * (`brand-icon-dark`).
 */
export function BrandIcon({ name, size = 16 }: { readonly name: BrandName; readonly size?: number }): ReactNode {
  const mark = BRAND_MARKS[name];
  return (
    <svg
      className={`brand-icon${dark(mark.hex) ? ' brand-icon-dark' : ''}`}
      style={{ color: `#${mark.hex}` }}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d={mark.path} />
    </svg>
  );
}

function dark(hex: string): boolean {
  const value = Number.parseInt(hex, 16);
  const [r, g, b] = [(value >> 16) & 255, (value >> 8) & 255, value & 255];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 105;
}

/** Platforms without a mark of their own (or without one in Simple Icons). */
const GENERIC: Record<string, LucideIcon> = {
  convertstudio: PanelsTopLeft,
  mini_cms: PanelsTopLeft,
  bitrix: Building2,
  opencart: ShoppingCart,
  static: FileCode2,
  custom: Boxes,
  app: AppWindow,
};

/**
 * What a site runs on, as a mark: a platform (`PLATFORMS`), what an agent
 * found on a server (`HOSTED_SITE_KIND_LABELS`) or an engine of the panel.
 */
export function PlatformIcon({ platform, size = 16 }: { readonly platform: string | null; readonly size?: number }): ReactNode {
  if (platform && Object.hasOwn(BRAND_MARKS, platform)) return <BrandIcon name={platform as BrandName} size={size} />;
  const Icon = (platform ? GENERIC[platform] : undefined) ?? CircleHelp;
  return <Icon size={size} aria-hidden="true" className="brand-icon brand-icon-generic" />;
}

/** The mark and the name together, as a table cell or a fact shows a platform. */
export function PlatformName({
  platform,
  label,
  size = 16,
}: {
  readonly platform: string | null;
  /** The name to print; the platform's own name when left out. */
  readonly label?: string;
  readonly size?: number;
}): ReactNode {
  return (
    <span className="platform-name">
      <PlatformIcon platform={platform} size={size} />
      <span>{label ?? platformLabel(platform) ?? 'Не определена'}</span>
    </span>
  );
}
